// Google and Meta campaigns on the spend page, and linking them to a client's website
// (public.dashboard_ad_campaigns / dashboard_link_ad_campaign, rnrinfra migration 20260930201849_ad_spend_links).
// Super admins only: unmatched spend belongs to no agency.

export type AdPlatform = 'Google Ads' | 'Meta Ads';
export type AdLevel = 'campaign' | 'account';
export type AdLink = { website_id: number; site: string | null } | null;
export type AdWebsite = { id: number; site: string; client: string | null; agency: string | null };
export type AdCampaign = {
  platform: AdPlatform; account_id: string; campaign_id: string; campaign_name: string | null;
  first_day: string; last_day: string; spend: number; clicks: number; conversions: number; unmatched_spend: number;
  website: { id: number; site: string; client: string | null; agency: string | null } | null;
  campaign_link: AdLink; account_link: AdLink;
};
export type AdAccount = {
  platform: AdPlatform; account_id: string; account_name: string | null; campaigns: number; spend: number; unmatched_spend: number;
  link: AdLink;
};
export type AdConnection = {
  provider: 'google_ads' | 'meta_ads'; last_success: string | null; last_failure: { at: string; reason: string | null } | null;
};
export type AdCampaignsData = {
  days: number; since: string; last_synced_on: string | null;
  totals: { spend: number; unmatched_spend: number; campaigns: number };
  connections: AdConnection[]; campaigns: AdCampaign[]; accounts: AdAccount[]; websites: AdWebsite[];
};
export type LinkResult = {
  platform: 'google' | 'meta'; level: AdLevel; external_id: string; website_id: number | null; website: string | null;
  previous_website_id: number | null; rows: number; spend: number;
};

export const platformKey = (platform: AdPlatform) => (platform === 'Google Ads' ? 'google' : 'meta');
export const providerLabel = (provider: AdConnection['provider']) => (provider === 'google_ads' ? 'Google Ads' : 'Meta Ads');
export const accountLabel = (platform: AdPlatform) => (platform === 'Google Ads' ? 'Google Ads account' : 'Meta ad account');

/** A connection is live when it last succeeded after its last failure. */
export function connectionState(c: AdConnection): { tone: 'green' | 'red' | 'muted'; text: string } {
  const ok = c.last_success ? Date.parse(c.last_success) : null;
  const bad = c.last_failure ? Date.parse(c.last_failure.at) : null;
  if (ok !== null && (bad === null || ok >= bad)) return { tone: 'green', text: 'Syncing' };
  if (bad !== null) {
    const reason = (c.last_failure?.reason || 'unknown error').replaceAll('_', ' ');
    return { tone: 'red', text: ok === null ? `Never synced: ${reason}` : `Failing: ${reason}` };
  }
  return { tone: 'muted', text: 'No sync recorded' };
}

/** "site.test · Client" for the picker and the table; websites with no client say so. */
export const siteLabel = (w: { site: string | null; client?: string | null }) =>
  `${w.site ?? 'Unknown website'}${w.client ? ` · ${w.client}` : ' · no client'}`;

/** Websites with a client first (by client), then the rest, for the picker. */
export function pickerGroups(websites: AdWebsite[]) {
  const byName = (a: AdWebsite, b: AdWebsite) => (a.client ?? '').localeCompare(b.client ?? '') || a.site.localeCompare(b.site);
  return {
    withClient: websites.filter((w) => w.client).sort(byName),
    withoutClient: websites.filter((w) => !w.client).sort((a, b) => a.site.localeCompare(b.site)),
  };
}

/** How a campaign is matched now: its own link, its account's, or not at all. */
export function matchedBy(c: AdCampaign): 'campaign' | 'account' | null {
  if (c.campaign_link) return 'campaign';
  if (c.account_link) return 'account';
  return null;
}

/** Unmatched spend first, then by spend. */
export const sortCampaigns = (rows: AdCampaign[]) =>
  [...rows].sort((a, b) => Number(b.unmatched_spend > 0) - Number(a.unmatched_spend > 0) || b.spend - a.spend || a.campaign_id.localeCompare(b.campaign_id));

/** What happened, in one sentence, after a save. */
export function linkNote(r: LinkResult, name: string) {
  const what = r.level === 'campaign' ? `Campaign ${name}` : `${name} (whole account)`;
  if (r.website_id === null) return `${what} is no longer linked. Its spend now follows ${r.level === 'campaign' ? 'its account’s link, if any' : 'any campaign links'}.`;
  const days = `${r.rows} day${r.rows === 1 ? '' : 's'}`;
  return `${what} is linked to ${r.website}. ${r.rows ? `${days} of spend (${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(r.spend)}) now count for it.` : 'Its spend will count for it as it syncs.'}`;
}
